import {
  Avatar,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoIcon from "@mui/icons-material/Info"; // Replace with your desired icon
import { useState, useEffect } from "react";
import {
  useGetCustCategoryAndPayMethodQuery,
  useGetPaymentSettingsQuery,
  useGetStoreUrlAndAccessTokenQuery,
  useGetStoreWebhookQuery,
} from "../../../redux/reducer/features/Dashboard/DashboardSlice";
import { useSelector } from "react-redux";
import { AppState } from "../../../redux/reducer/rootReducer";
import { PartsPortalType } from "../../../models/features/ShopifySetting/ShoppingPortal/ShoppingPortal";

export interface CustCategoryAndPayMethodState {
  customerCategoryAssigned: boolean;
  dmsPaymentMethodAssigned: boolean;
  message: string;
  status: number;
}

export interface StoreURlAndAccessTokenState {
  isStoreUrlValid: boolean;
  isAccessTokenValid: boolean;
  isUrlAndTokenVerified: boolean;
  message: string;
  status: number;
}

export interface ShopifyWebHookState {
  hasMultipleWebhooks: boolean;
  message: string;
  status: number;
}

export interface PaymentSettingsState {
  paymentSettingVerified: boolean;
  partsPortalExist: boolean;
  paymentType: string;
  message: string;
  status: number;
}

export interface PartsPortalState {
  partsPortalExist: boolean;
  message: string;
  status: number;
}

interface CustomTooltipProps extends TooltipProps {
  bgColor?: string;
  textColor?: string;
}

const CustomTooltip = styled(
  ({ className, bgColor, textColor, ...props }: CustomTooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  )
)<CustomTooltipProps>(({ theme, bgColor, textColor }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: bgColor,
    color: textColor,
    boxShadow: theme.shadows[1],
  },
}));

export const AppHealth = () => {
  const [showRefreshTooltip, setShowRefreshTooltip] = useState(false);
  const [totalHealthParams, setTotalHealthParams] = useState(3);
  const [showPartsPortalHealth, setShowPartsPortalHealth] = useState(false);
  const { dmsSetup } = useSelector((state: AppState) => state.authReducer);
  const [successfulHealth, setSuccessfulHealth] = useState(0);
  const partsPortalSetting = useSelector(
    (state: AppState) => state.shoppingPortalReducer
  );

  const [custCategoryAndPayMethodState, setCustCategoryAndPayMethodState] =
    useState<{
      customerCategoryAssigned: boolean;
      dmsPaymentMethodAssigned: boolean;
      message: string;
      status: number;
    }>();
  const [storeURlAndAccessTokenState, setStoreURlAndAccessTokenState] =
    useState<{
      isStoreUrlValid: boolean;
      isAccessTokenValid: boolean;
      isUrlAndTokenVerified: boolean;
      message: string;
      status: number;
    }>();
  const [storeWebhookState, setStoreWebhookState] = useState<{
    hasMultipleWebhooks: boolean;
    message: string;
    status: number;
  }>();
  const [paymentSettingsState, setPaymentSettingsState] = useState<{
    paymentSettingVerified: boolean;
    partsPortalExist: boolean;
    paymentType: string;
    message: string;
    status: number;
  }>();
  const [partsPortalState, setPartsPortalState] = useState<{
    partsPortalExist: boolean;
    message: string;
    status: number;
  }>();

  const {
    data: custCategoryAndPayMethodData,
    isLoading: custCategoryAndPayMethodLoading,
    isFetching: custCategoryAndPayMethodFetching,
    refetch: refetchCustCategoryAndPayMethod,
  } = useGetCustCategoryAndPayMethodQuery(undefined, {
    skip: dmsSetup?.id === undefined ? true : false,
  });
  const {
    data: storeURlAndAccessTokenData,
    isLoading: storeURlAndAccessTokenLoading,
    isFetching: storeURlAndAccessTokenFetching,
    refetch: refetchStoreUUrlAndAccessToken,
  } = useGetStoreUrlAndAccessTokenQuery(undefined, {
    skip: dmsSetup?.id === undefined ? true : false,
  });
  const {
    data: storeWebhookData,
    isLoading: storeWebhookLoading,
    refetch: refetchStoreWebhook,
    isFetching: storeWebhookFetching,
  } = useGetStoreWebhookQuery(undefined, {
    skip: dmsSetup?.id === undefined ? true : false,
  });
  const {
    data: paymentSettingsData,
    isLoading: paymentSettingsLoading,
    isFetching: paymentSettingsFetching,
    refetch: refetchPaymentSettings,
  } = useGetPaymentSettingsQuery(undefined, {
    skip: dmsSetup?.id === undefined ? true : false,
  });

  useEffect(() => {
    const successfulConditions = countSuccessfulHealthConditions();
    setSuccessfulHealth(successfulConditions);
  }, [
    storeURlAndAccessTokenState,
    custCategoryAndPayMethodState,
    paymentSettingsState,
    storeWebhookState,
    partsPortalState,
  ]);

  useEffect(() => {
    if (custCategoryAndPayMethodData && custCategoryAndPayMethodData.entity) {
      setCustCategoryAndPayMethodState({
        ...custCategoryAndPayMethodData.entity,
        message: custCategoryAndPayMethodData.message || "",
        status: custCategoryAndPayMethodData.status,
      });
    }
  }, [custCategoryAndPayMethodData]);

  useEffect(() => {
    if (storeURlAndAccessTokenData && storeURlAndAccessTokenData.entity) {
      setStoreURlAndAccessTokenState({
        ...storeURlAndAccessTokenData.entity,
        message: storeURlAndAccessTokenData.message || "",
        status: storeURlAndAccessTokenData.status,
      });
    }
  }, [storeURlAndAccessTokenData]);
  useEffect(() => {
    if (storeWebhookData && storeWebhookData.entity) {
      setStoreWebhookState({
        ...storeWebhookData.entity,
        message: storeWebhookData.message || "",
        status: storeWebhookData.status,
      });
    }
  }, [storeWebhookData]);

  useEffect(() => {
    if (paymentSettingsData && paymentSettingsData.entity) {
      setPaymentSettingsState({
        ...paymentSettingsData.entity,
        message: paymentSettingsData.message || "",
        status: paymentSettingsData.status,
      });

      if (paymentSettingsData.entity.partsPortalExist === true) {
        setPartsPortalState({
          partsPortalExist: true,
          message: "Verified",
          status: 200,
        });
      } else {
        setPartsPortalState({
          partsPortalExist: true,
          message: "Parts Portal does not exist.",
          status: 404,
        });
      }
    }
  }, [paymentSettingsData]);

  const countSuccessfulHealthConditions = () => {
    let count = 0;
    if (
      storeURlAndAccessTokenState?.isUrlAndTokenVerified &&
      storeURlAndAccessTokenState?.status === 200
    )
      count++;
    if (
      custCategoryAndPayMethodState?.customerCategoryAssigned &&
      custCategoryAndPayMethodState.dmsPaymentMethodAssigned &&
      custCategoryAndPayMethodState.status === 200
    )
      count++;
    if (
      storeWebhookState?.hasMultipleWebhooks === false &&
      storeWebhookState.status === 200
    )
      count++;
    if (
      paymentSettingsState?.paymentSettingVerified &&
      paymentSettingsState.status === 200 &&
      showPartsPortalHealth
    )
      count++;
    if (
      partsPortalState?.partsPortalExist &&
      partsPortalState.status === 200 &&
      showPartsPortalHealth
    )
      count++;
    return count;
  };

  const GetRefreshedAppHealth = () => {
    setShowRefreshTooltip(false);
    refetchCustCategoryAndPayMethod();
    refetchStoreUUrlAndAccessToken();
    refetchStoreWebhook();
    if (showPartsPortalHealth) {
      refetchPaymentSettings();
    }
  };
  useEffect(() => {
    if (
      partsPortalSetting &&
      partsPortalSetting?.isPortalEnabled === true &&
      partsPortalSetting.portalType === PartsPortalType.StandAlone
    ) {
      setTotalHealthParams(5);
      setShowPartsPortalHealth(true);
    } else {
      setTotalHealthParams(3);
      setShowPartsPortalHealth(false);
    }
  }, [partsPortalSetting]);
  return (
    <Card>
      <CardContent sx={{ pb: 0 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display={"flex"} alignItems={"center"}>
            <Typography color="textSecondary" variant="overline">
              App Health
            </Typography>
            <Tooltip
              title={"Click to get latest data!"}
              open={showRefreshTooltip}
              disableHoverListener
              onMouseEnter={() => setShowRefreshTooltip(true)}
              onMouseLeave={() => setShowRefreshTooltip(false)}
            >
              <IconButton
                disabled={
                  !dmsSetup?.id ||
                  custCategoryAndPayMethodFetching ||
                  storeURlAndAccessTokenFetching ||
                  storeWebhookFetching ||
                  paymentSettingsFetching
                }
                onClick={GetRefreshedAppHealth}
                size="small"
              >
                {custCategoryAndPayMethodFetching ||
                storeURlAndAccessTokenFetching ||
                storeWebhookFetching ||
                paymentSettingsFetching ? (
                  <CircularProgress size={"1rem"} />
                ) : (
                  <RefreshIcon sx={{ fontSize: "1rem" }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
          <Typography fontWeight={"bold"} variant="inherit">
            <span style={{ color: "green" }}>{successfulHealth}</span> /{" "}
            {totalHealthParams}
          </Typography>
        </Box>
        {dmsSetup?.id ? (
          custCategoryAndPayMethodLoading ||
          storeURlAndAccessTokenLoading ||
          storeWebhookLoading ||
          paymentSettingsLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 80,
              }}
            >
              <Typography style={{ fontSize: "0.75rem" }}>
                Loading data...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ height: 80, overflowX: "auto" }}>
              <Grid container spacing={0.5}>
                <Grid item xs={6}>
                  <ListItem sx={{ p: 0 }}>
                    <ListItemIcon sx={{ minWidth: "25px" }}>
                      <CustomTooltip
                        title={storeURlAndAccessTokenState?.message || ""}
                        bgColor="white"
                        textColor={
                          storeURlAndAccessTokenState?.isUrlAndTokenVerified &&
                          storeURlAndAccessTokenState?.status === 200
                            ? "#2e7d32"
                            : "#d32f2f"
                        }
                      >
                        <Avatar
                          sx={{
                            backgroundColor:
                              storeURlAndAccessTokenState?.isUrlAndTokenVerified
                                ? "success.main"
                                : "error.main",
                            height: 18,
                            width: 18,
                          }}
                        >
                          {storeURlAndAccessTokenState?.isUrlAndTokenVerified &&
                          storeURlAndAccessTokenState?.status === 200 ? (
                            <CheckIcon sx={{ fontSize: "0.75rem" }} />
                          ) : (
                            <ClearIcon sx={{ fontSize: "0.75rem" }} />
                          )}
                        </Avatar>
                      </CustomTooltip>
                    </ListItemIcon>
                    <ListItemText>
                      <Typography
                        color="textSecondary"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Store URL & Token
                      </Typography>
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ p: 0 }}>
                    <ListItemIcon sx={{ minWidth: "25px" }}>
                      <CustomTooltip
                        title={custCategoryAndPayMethodState?.message || ""}
                        bgColor="white"
                        textColor={
                          custCategoryAndPayMethodState?.customerCategoryAssigned &&
                          custCategoryAndPayMethodState.dmsPaymentMethodAssigned &&
                          custCategoryAndPayMethodState.status === 200
                            ? "#2e7d32"
                            : "#d32f2f"
                        }
                      >
                        <Avatar
                          sx={{
                            backgroundColor:
                              custCategoryAndPayMethodState?.customerCategoryAssigned &&
                              custCategoryAndPayMethodState.dmsPaymentMethodAssigned &&
                              custCategoryAndPayMethodState.status === 200
                                ? "success.main"
                                : "error.main",
                            height: 18,
                            width: 18,
                          }}
                        >
                          {custCategoryAndPayMethodState?.customerCategoryAssigned &&
                          custCategoryAndPayMethodState.dmsPaymentMethodAssigned &&
                          custCategoryAndPayMethodState.status === 200 ? (
                            <CheckIcon sx={{ fontSize: "0.75rem" }} />
                          ) : (
                            <ClearIcon sx={{ fontSize: "0.75rem" }} />
                          )}
                        </Avatar>
                      </CustomTooltip>
                    </ListItemIcon>
                    <ListItemText>
                      <Typography
                        color="textSecondary"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Order Settings
                      </Typography>
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ p: 0 }}>
                    <ListItemIcon sx={{ minWidth: "25px" }}>
                      <CustomTooltip
                        title={storeWebhookState?.message || ""}
                        bgColor="white"
                        textColor={
                          storeWebhookState?.hasMultipleWebhooks === false &&
                          storeWebhookState.status === 200
                            ? "#2e7d32"
                            : "#d32f2f"
                        }
                      >
                        <Avatar
                          sx={{
                            backgroundColor:
                              storeWebhookState?.hasMultipleWebhooks ===
                                false && storeWebhookState.status === 200
                                ? "success.main"
                                : "error.main",
                            height: 18,
                            width: 18,
                          }}
                        >
                          {storeWebhookState?.status === 200 ? (
                            storeWebhookState.hasMultipleWebhooks ? (
                              <PriorityHighIcon sx={{ fontSize: "0.75rem" }} />
                            ) : (
                              <CheckIcon sx={{ fontSize: "0.75rem" }} />
                            )
                          ) : (
                            <ClearIcon sx={{ fontSize: "0.75rem" }} />
                          )}
                        </Avatar>
                      </CustomTooltip>
                    </ListItemIcon>
                    <ListItemText>
                      <Typography
                        color="textSecondary"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        <Box display="flex" alignItems="center">
                          <span>Store Webhook</span>
                          <Tooltip title="This webhook is to receive orders from the store.">
                            <InfoIcon
                              fontSize="small"
                              style={{ marginLeft: 4 }}
                            />
                          </Tooltip>
                        </Box>
                      </Typography>
                    </ListItemText>
                  </ListItem>
                </Grid>
                <Grid item xs={6}>
                  {showPartsPortalHealth ? (
                    <>
                      <ListItem
                        sx={{
                          p: 0,
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: "25px" }}>
                          <CustomTooltip
                            title={paymentSettingsState?.message || ""}
                            bgColor="white"
                            textColor={
                              paymentSettingsState?.paymentSettingVerified &&
                              paymentSettingsState.status === 200
                                ? "#2e7d32"
                                : paymentSettingsState?.partsPortalExist
                                ? "#d32f2f"
                                : "#0288d1"
                            }
                          >
                            <Avatar
                              sx={{
                                backgroundColor:
                                  paymentSettingsState?.paymentSettingVerified &&
                                  paymentSettingsState.status === 200
                                    ? "success.main"
                                    : paymentSettingsState?.partsPortalExist
                                    ? "error.main"
                                    : "info.main",
                                height: 18,
                                width: 18,
                              }}
                            >
                              {paymentSettingsState?.paymentSettingVerified &&
                              paymentSettingsState.status === 200 ? (
                                <CheckIcon sx={{ fontSize: "0.75rem" }} />
                              ) : paymentSettingsState?.partsPortalExist ? (
                                <ClearIcon sx={{ fontSize: "0.75rem" }} />
                              ) : (
                                <PriorityHighIcon
                                  sx={{ fontSize: "0.75rem" }}
                                />
                              )}
                            </Avatar>
                          </CustomTooltip>
                        </ListItemIcon>
                        <ListItemText>
                          <Typography
                            color="textSecondary"
                            sx={{ fontSize: "0.75rem" }}
                          >
                            Payment Settings
                          </Typography>
                        </ListItemText>
                      </ListItem>
                      <ListItem
                        sx={{
                          p: 0,
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: "25px" }}>
                          <CustomTooltip
                            title={partsPortalState?.message || ""}
                            bgColor="white"
                            textColor={
                              partsPortalState?.partsPortalExist &&
                              partsPortalState.status === 200
                                ? "#2e7d32"
                                : "#d32f2f"
                            }
                          >
                            <Avatar
                              sx={{
                                backgroundColor:
                                  partsPortalState?.partsPortalExist &&
                                  partsPortalState.status === 200
                                    ? "success.main"
                                    : "info.main",
                                height: 18,
                                width: 18,
                              }}
                            >
                              {partsPortalState?.partsPortalExist &&
                              partsPortalState.status === 200 ? (
                                <CheckIcon sx={{ fontSize: "0.75rem" }} />
                              ) : (
                                <PriorityHighIcon
                                  sx={{ fontSize: "0.75rem" }}
                                />
                              )}
                            </Avatar>
                          </CustomTooltip>
                        </ListItemIcon>
                        <ListItemText>
                          <Typography
                            color="textSecondary"
                            sx={{ fontSize: "0.75rem" }}
                          >
                            Parts Portal
                          </Typography>
                        </ListItemText>
                      </ListItem>
                    </>
                  ) : null}
                </Grid>
              </Grid>
            </Box>
          )
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 80,
            }}
          >
            <Typography color="textPrimary" variant="body1">
              N/A
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
